import { FormEvent, useEffect, useState } from 'react';
import { Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { ManagementModal } from '../../user-management/UserManagementModals';
import {
  AddOnDto,
  CreateAddOnDto,
  CreateTierDto,
  PlatformAddOn,
  TierAddOnDto,
  TierDto,
  UpdateAddOnDto,
  UpdateTierDto,
} from '../../../types/userManagement';
import {
  useSubscriptionAddOns,
  useSubscriptionTiers,
  useTierAddOns,
  useUserManagementMutations,
} from '../../../hooks/useUserManagement';
import { getApiErrorMessage } from '../../../api/apiClient';
import Spinner from '../../Spinner';

type CatalogAction =
  | { type: 'tier'; item?: TierDto }
  | { type: 'addon'; item?: AddOnDto }
  | { type: 'relationships'; item: TierDto }
  | null;

const PLATFORM_ADD_ON_LABELS: Record<PlatformAddOn, string> = {
  [PlatformAddOn.Website]: 'Website',
  [PlatformAddOn.JobBoard]: 'Job Board',
  [PlatformAddOn.BillingAndProposals]: 'Billing & Proposals',
  [PlatformAddOn.BookingAndScheduling]: 'Booking & Scheduling',
  [PlatformAddOn.Forms]: 'Forms',
};

const getAddOnLabel = (addOn: AddOnDto) =>
  PLATFORM_ADD_ON_LABELS[addOn.code] ??
  addOn.name ??
  `Unknown module (${addOn.code})`;

const formatPrice = (value: number) => value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

export function SubscriptionCatalogSettings({ mode }: { mode: 'tiers' | 'addons' }) {
  const tiersQuery = useSubscriptionTiers();
  const addOnsQuery = useSubscriptionAddOns();
  const mutations = useUserManagementMutations();
  const [action, setAction] = useState<CatalogAction>(null);
  const [error, setError] = useState('');

  const close = () => {
    setAction(null);
    setError('');
  };

  const run = async (operation: () => Promise<unknown>, successMessage: string) => {
    try {
      await operation();
      toast.success(successMessage);
      close();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Unable to save subscription catalog changes.'));
    }
  };

  if (tiersQuery.isLoading || addOnsQuery.isLoading) {
    return <div className="flex items-center justify-center rounded-2xl border border-strivre-lightnavy bg-strivre-softgrey p-8 text-slate-500"><Spinner size="h-5 w-5" /><span className="ml-2">Loading subscription catalog...</span></div>;
  }

  if (tiersQuery.isError || addOnsQuery.isError) {
    return <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">{getApiErrorMessage(tiersQuery.error ?? addOnsQuery.error, 'Unable to load the subscription catalog.')}</div>;
  }

  const tiers = tiersQuery.data ?? [];
  const addOns = addOnsQuery.data ?? [];

  const platformAddOns = Object.values(PlatformAddOn).filter(
    (value): value is PlatformAddOn =>
      typeof value === 'number'
  );

  const configuredAddOnCodes = new Set(
    addOns.map((addOn) => addOn.code)
  );

  const availableAddOnCodes = platformAddOns.filter(
    (code) => !configuredAddOnCodes.has(code)
  );

  const canCreateAddOn = availableAddOnCodes.length > 0;

  return (
    <div className="space-y-6 text-left">
      {mode === 'tiers' && <CatalogTable
        title="Subscription Tiers"
        columns={['Name', 'Description', 'Monthly Price', 'Annual Price', 'Active', 'Display Order']}
        onCreate={() => setAction({ type: 'tier' })}
      >
        {tiers.map((tier) => (
          <tr key={tier.id} className="border-t border-slate-100">
            <TableCell>{tier.name}</TableCell>
            <TableCell>{tier.description ?? '-'}</TableCell>
            <TableCell>{formatPrice(tier.monthlyPrice)}</TableCell>
            <TableCell>{formatPrice(tier.annualPrice)}</TableCell>
            <TableCell>{tier.isActive ? 'Yes' : 'No'}</TableCell>
            <TableCell>{tier.displayOrder}</TableCell>
            <Actions onEdit={() => setAction({ type: 'tier', item: tier })} onConfigure={() => setAction({ type: 'relationships', item: tier })} onDelete={() => run(() => mutations.deleteTier.mutateAsync(tier.id), 'Tier deleted.')} />
          </tr>
        ))}
      </CatalogTable>}

      {mode === 'addons' && (
        <CatalogTable
          title="Subscription Add-Ons"
          columns={[
            'Module',
            'Description',
            'Monthly Price',
            'Annual Price',
            'Active',
            'Purchasable Separately',
            'Display Order',
          ]}
          onCreate={() =>
            setAction({
              type: 'addon',
            })
          }
          createDisabled={!canCreateAddOn}
          createTitle={
            canCreateAddOn
              ? 'Add a platform add-on'
              : 'All platform add-ons are already configured'
          }
        >
        {addOns.map((addOn) => (
          <tr key={addOn.id} className="border-t border-slate-100">
            <TableCell>
              <div>
                <p className="font-semibold text-slate-700">
                  {getAddOnLabel(addOn)}
                </p>

                <p className="mt-0.5 text-xs text-slate-400">
                  {PlatformAddOn[addOn.code] ?? `Code ${addOn.code}`}
                </p>
              </div>
            </TableCell>
            <TableCell>{addOn.description ?? '-'}</TableCell>
            <TableCell>{formatPrice(addOn.monthlyPrice)}</TableCell>
            <TableCell>{formatPrice(addOn.annualPrice)}</TableCell>
            <TableCell>{addOn.isActive ? 'Yes' : 'No'}</TableCell>
            <TableCell>{addOn.isPurchasableSeparately ? 'Yes' : 'No'}</TableCell>
            <TableCell>{addOn.displayOrder}</TableCell>
            <Actions
              onEdit={() =>
                setAction({
                  type: 'addon',
                  item: addOn,
                })
              }
              onDelete={() =>
                run(
                  () =>
                    mutations.deleteAddOn.mutateAsync(
                      addOn.id
                    ),
                  'Add-on deleted.'
                )
              }
            />
          </tr>
        ))}
      </CatalogTable>)}

      {action?.type === 'tier' && (
        <TierFormModal
          item={action.item}
          error={error}
          loading={
            mutations.createTier.isPending ||
            mutations.updateTier.isPending
          }
          onClose={close}
          onSave={(request) => {
            if (action.item) {
              const updateRequest: UpdateTierDto = {
                name: request.name,
                description: request.description,
                monthlyPrice: request.monthlyPrice,
                annualPrice: request.annualPrice,
                displayOrder: request.displayOrder,
                isActive: request.isActive ?? true,
              };

              return run(
                () =>
                  mutations.updateTier.mutateAsync({
                    tierId: action.item!.id,
                    request: updateRequest,
                  }),
                'Tier updated.'
              );
            }

            const createRequest: CreateTierDto = {
              name: request.name,
              description: request.description,
              monthlyPrice: request.monthlyPrice,
              annualPrice: request.annualPrice,
              displayOrder: request.displayOrder,
              isActive: request.isActive,
            };

            return run(
              () =>
                mutations.createTier.mutateAsync(
                  createRequest
                ),
              'Tier created.'
            );
          }}
        />
      )}
      {action?.type === 'addon' && (
        <AddOnFormModal
          item={action.item}
          availableCodes={availableAddOnCodes}
          error={error}
          loading={
            mutations.createAddOn.isPending ||
            mutations.updateAddOn.isPending
          }
          onClose={close}
          onSave={(request) => {
            if (action.item) {
              return run(
                () =>
                  mutations.updateAddOn.mutateAsync({
                    addOnId: action.item!.id,
                    request: request as UpdateAddOnDto,
                  }),
                'Add-on updated.'
              );
            }

            return run(
              () =>
                mutations.createAddOn.mutateAsync(
                  request as CreateAddOnDto
                ),
              'Add-on created.'
            );
          }}
        />
      )}
      {action?.type === 'relationships' && <TierAddOnsModal tier={action.item} addOns={addOns} error={error} loading={mutations.assignTierAddOn.isPending || mutations.removeTierAddOn.isPending} onClose={close} onAssign={(request) => run(() => mutations.assignTierAddOn.mutateAsync({ tierId: action.item.id, request }), 'Add-on assigned to tier.')} onRemove={(addOnId) => run(() => mutations.removeTierAddOn.mutateAsync({ tierId: action.item.id, addOnId }), 'Add-on removed from tier.')} />}
    </div>
  );
}

function CatalogTable({
  title,
  columns,
  onCreate,
  createDisabled = false,
  createTitle,
  children,
}: {
  title: string;
  columns: string[];
  onCreate?: () => void;
  createDisabled?: boolean;
  createTitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white">
      <div className="flex items-center justify-between border-b border-strivre-lightnavy p-5">
        <h3 className="text-base font-bold text-slate-900">
          {title}
        </h3>

        {onCreate && (
          <button
            type="button"
            onClick={onCreate}
            disabled={createDisabled}
            title={createTitle}
            className="btn-primary btn-md inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
            Create
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                >
                  {column}
                </th>
              ))}

              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {children}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TableCell({ children }: { children: React.ReactNode }) {
  return <td className="max-w-56 px-4 py-3 text-slate-600">{children}</td>;
}

function Actions({
  onEdit,
  onConfigure,
  onDelete,
}: {
  onEdit: () => void;
  onConfigure?: () => void;
  onDelete?: () => void;
}) {
  return (
    <td className="px-4 py-3">
      <div className="flex justify-end gap-1">
        <button
          type="button"
          title="Edit"
          onClick={onEdit}
          className="rounded-lg p-2 text-slate-500 hover:bg-blue-50 hover:text-blue-600"
        >
          <Pencil size={16} />
        </button>

        {onConfigure && (
          <button
            type="button"
            title="Configure tier add-ons"
            onClick={onConfigure}
            className="rounded-lg p-2 text-slate-500 hover:bg-strivre-softgrey hover:text-strivre-burgundy"
          >
            <SlidersHorizontal size={16} />
          </button>
        )}

        {onDelete && (
          <button
            type="button"
            title="Delete"
            onClick={onDelete}
            className="rounded-lg p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </td>
  );
}

function TierFormModal({
  item,
  onClose,
  onSave,
  loading,
  error,
}: {
  item?: TierDto;
  onClose: () => void;
  onSave: (request: CreateTierDto | UpdateTierDto) => void;
  loading: boolean;
  error: string;
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    monthlyPrice: '0',
    annualPrice: '0',
    isActive: true,
    displayOrder: '0',
  });

  useEffect(() => {
    setForm({
      name: item?.name ?? '',
      description: item?.description ?? '',
      monthlyPrice: String(item?.monthlyPrice ?? 0),
      annualPrice: String(item?.annualPrice ?? 0),
      isActive: item?.isActive ?? true,
      displayOrder: String(item?.displayOrder ?? 0),
    });
  }, [item]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const request = {
      name: form.name,
      description: form.description || null,
      monthlyPrice: Number(form.monthlyPrice),
      annualPrice: Number(form.annualPrice),
      displayOrder: Number(form.displayOrder),
      isActive: form.isActive,
    };

    onSave(request);
  };

  return (
    <CatalogModal
      title={item ? 'Edit tier' : 'Create tier'}
      onClose={onClose}
      onSubmit={submit}
      loading={loading}
      error={error}
    >
      <TextInput
        label="Name"
        value={form.name}
        onChange={(name) =>
          setForm({
            ...form,
            name,
          })
        }
      />

      <TextInput
        label="Description"
        value={form.description}
        required={false}
        onChange={(description) =>
          setForm({
            ...form,
            description,
          })
        }
      />

      <NumberInputs
        form={form}
        setForm={setForm}
      />

      <Toggle
        label="Active"
        checked={form.isActive}
        onChange={(isActive) =>
          setForm({
            ...form,
            isActive,
          })
        }
      />
    </CatalogModal>
  );
}

function AddOnFormModal({
  item,
  availableCodes,
  onClose,
  onSave,
  loading,
  error,
}: {
  item?: AddOnDto;
  availableCodes: PlatformAddOn[];
  onClose: () => void;
  onSave: (
    request: CreateAddOnDto | UpdateAddOnDto
  ) => void;
  loading: boolean;
  error: string;
}) {
  const [code, setCode] = useState<PlatformAddOn | ''>('');

  const [form, setForm] = useState({
    description: '',
    monthlyPrice: '0',
    annualPrice: '0',
    isActive: true,
    isPurchasableSeparately: true,
    displayOrder: '0',
  });

  useEffect(() => {
    if (item) {
      setCode(item.code);

      setForm({
        description: item.description ?? '',
        monthlyPrice: String(item.monthlyPrice ?? 0),
        annualPrice: String(item.annualPrice ?? 0),
        isActive: item.isActive,
        isPurchasableSeparately:
          item.isPurchasableSeparately,
        displayOrder: String(
          item.displayOrder ?? 0
        ),
      });

      return;
    }

    setCode('');

    setForm({
      description: '',
      monthlyPrice: '0',
      annualPrice: '0',
      isActive: true,
      isPurchasableSeparately: true,
      displayOrder: '0',
    });
  }, [item]);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!item && code === '') {
      return;
    }

    const base = {
      description:
        form.description || null,

      monthlyPrice:
        Number(form.monthlyPrice),

      annualPrice:
        Number(form.annualPrice),

      displayOrder:
        Number(form.displayOrder),

      isActive:
        form.isActive,

      isPurchasableSeparately:
        form.isPurchasableSeparately,
    };

    if (item) {
      const request: UpdateAddOnDto = {
        ...base,

        code: item.code,

        name:
          PLATFORM_ADD_ON_LABELS[item.code] ??
          item.name,
      };

      onSave(request);

      return;
    }

    const selectedCode =
      code as PlatformAddOn;

    const request: CreateAddOnDto = {
      ...base,
      code: selectedCode,
      name:
        PLATFORM_ADD_ON_LABELS[
          selectedCode
        ],
    };

    onSave(request);
  };

  return (
    <CatalogModal
      title={
        item
          ? 'Edit add-on'
          : 'Create add-on'
      }
      onClose={onClose}
      onSubmit={submit}
      loading={loading}
      error={error}
    >
      {item ? (
        <div>
          <label className="block text-sm font-semibold text-slate-700">
            Module
          </label>

          <div className="mt-1 rounded-lg border border-strivre-lightnavy bg-slate-50 px-3 py-3">
            <p className="text-sm font-semibold text-slate-800">
              {getAddOnLabel(item)}
            </p>

            <p className="mt-0.5 text-xs text-slate-400">
              {PlatformAddOn[item.code]}
            </p>
          </div>
        </div>
      ) : (
        <label className="block text-sm font-semibold text-slate-700">
          Module

          <select
            required
            className="input-base mt-1 w-full"
            value={code}
            onChange={(event) =>
              setCode(
                Number(
                  event.target.value
                ) as PlatformAddOn
              )
            }
          >
            <option value="">
              Select a module
            </option>

            {availableCodes.map(
              (platformAddOn) => (
                <option
                  key={platformAddOn}
                  value={platformAddOn}
                >
                  {
                    PLATFORM_ADD_ON_LABELS[
                      platformAddOn
                    ]
                  }
                </option>
              )
            )}
          </select>
        </label>
      )}

      <TextInput
        label="Description"
        value={form.description}
        required={false}
        onChange={(description) =>
          setForm({
            ...form,
            description,
          })
        }
      />

      <NumberInputs
        form={form}
        setForm={setForm}
      />

      <Toggle
        label="Active"
        checked={form.isActive}
        onChange={(isActive) =>
          setForm({
            ...form,
            isActive,
          })
        }
      />

      <Toggle
        label="Purchasable separately"
        checked={
          form.isPurchasableSeparately
        }
        onChange={(
          isPurchasableSeparately
        ) =>
          setForm({
            ...form,
            isPurchasableSeparately,
          })
        }
      />
    </CatalogModal>
  );
}

function NumberInputs({ form, setForm }: { form: { monthlyPrice: string; annualPrice: string; displayOrder: string }; setForm: (value: any) => void }) {
  return <div className="grid gap-4 sm:grid-cols-3"><TextInput label="Monthly price" type="number" value={form.monthlyPrice} onChange={(monthlyPrice) => setForm({ ...form, monthlyPrice })} /><TextInput label="Annual price" type="number" value={form.annualPrice} onChange={(annualPrice) => setForm({ ...form, annualPrice })} /><TextInput label="Display order" type="number" value={form.displayOrder} onChange={(displayOrder) => setForm({ ...form, displayOrder })} /></div>;
}

function TierAddOnsModal({ tier, addOns, onClose, onAssign, onRemove, loading, error }: { tier: TierDto; addOns: AddOnDto[]; onClose: () => void; onAssign: (request: TierAddOnDto) => void; onRemove: (addOnId: number) => void; loading: boolean; error: string }) {
  const assignedQuery = useTierAddOns(tier.id);
  const [addOnId, setAddOnId] = useState('');
  const [isIncluded, setIsIncluded] = useState(false);
  const assigned = assignedQuery.data ?? [];
  const available = addOns.filter(
    (addOn) =>
      addOn.isActive &&
      !assigned.some(
        (relation) =>
          relation.addOnId === addOn.id
      )
  );
  return <ManagementModal title={`Configure ${tier.name} add-ons`} isOpen onClose={onClose}><div className="space-y-4 p-6 text-left">{assignedQuery.isLoading ? <div className="flex justify-center py-4"><Spinner size="h-5 w-5" /></div> : <><div className="max-h-48 space-y-2 overflow-y-auto rounded-lg border border-strivre-lightnavy p-3">{assigned.length ? assigned.map((relation) => { const addOn = addOns.find((item) => item.id === relation.addOnId); return <div key={relation.addOnId} className="flex items-center justify-between text-sm"><span>{addOn?.name ?? `Add-on ${relation.addOnId}`} {relation.isIncluded ? '(Included)' : ''}</span><button type="button" disabled={loading} onClick={() => onRemove(relation.addOnId)} className="text-strivre-burgundy hover:underline">Remove</button></div>; }) : <p className="text-sm text-slate-500">No add-ons assigned.</p>}</div><form className="space-y-3" onSubmit={(event) => { event.preventDefault(); onAssign({ tierId: tier.id, addOnId: Number(addOnId), isIncluded }); }}><label className="block text-sm font-semibold text-slate-700">Add-on<select required className="input-base mt-1 w-full" value={addOnId} onChange={(event) => setAddOnId(event.target.value)}><option value="">Select an add-on</option>{available.map((addOn) => <option key={addOn.id} value={addOn.id}>{addOn.name}</option>)}</select></label><Toggle label="Included with this tier" checked={isIncluded} onChange={setIsIncluded} /><button type="submit" disabled={loading || !addOnId} className="btn-primary btn-md">Assign add-on</button></form></>}{error && <p className="text-sm text-red-600">{error}</p>}</div></ManagementModal>;
}

function CatalogModal({ title, onClose, onSubmit, loading, error, children }: { title: string; onClose: () => void; onSubmit: (event: FormEvent) => void; loading: boolean; error: string; children: React.ReactNode }) {
  return <ManagementModal title={title} isOpen onClose={onClose}><form onSubmit={onSubmit} className="space-y-4 p-6 text-left">{children}{error && <p className="text-sm text-red-600">{error}</p>}<footer className="flex gap-3 pt-3"><button type="button" className="btn-secondary btn-md flex-1" onClick={onClose} disabled={loading}>Cancel</button><button type="submit" className="btn-primary btn-md flex-1" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button></footer></form></ManagementModal>;
}

function TextInput({ label, value, onChange, type = 'text', required = true }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean }) {
  return <label className="block text-sm font-semibold text-slate-700">{label}<input required={required} type={type} min={type === 'number' ? 0 : undefined} className="input-base mt-1 w-full" value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />{label}</label>;
}
